import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Card, Col, Divider, Form, Input, InputNumber, Modal, Row, Select, Space, Spin, Table, Tag,  message,Flex, Upload
} from 'antd';
import {
  DeleteOutlined, EditOutlined, GlobalOutlined, PauseCircleOutlined, PlayCircleOutlined, PlusOutlined, InboxOutlined
} from '@ant-design/icons';
import { createRegion, deleteRegion, fetchCountries, fetchRegions, toggleRegionStatus, updateRegion, importRegions } from '../../utils/api';
import { downloadRegionTemplate } from '../../utils/csvTemplate';

const { TextArea } = Input;
const { Option } = Select;

/* -------------------- 组件 -------------------- */
export default function RegionManagement() {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  /* 模态框 */
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  /* 国家 */
  const [, setCountries] = useState([]);
  const [selected, setSelected] = useState([]);          // 已选国家
  const [available, setAvailable] = useState([]);        // 可选国家

  /* 删除确认 */
  const [delId, setDelId] = useState(null);
  const [showDel, setShowDel] = useState(false);

  /* 批量导入 */
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [importRows, setImportRows] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importLoading, setImportLoading] = useState(false);

  /* -------------------- 数据加载 -------------------- */
  const loadRegions = async (p = page, ps = pageSize) => {
    setLoading(true);
    try {
      const { list: data, total: t } = await fetchRegions(p, ps);
      setRegions(data || []);
      setTotal(t || 0);
    } finally {
      setLoading(false);
    }
  };

  const loadCountries = async (regionId, regionCountriesArg = []) => {
    const list = await fetchCountries({ excludeAssociated: true, excludeRegionId: regionId });
    setCountries(list);
    const regionCountries = Array.isArray(regionCountriesArg) ? regionCountriesArg : [];
    const selectedIds = regionCountries.map(c => c.id || c._id).filter(Boolean);
    const selectedCodes = regionCountries.map(c => c.code).filter(Boolean);
    const availableList = list.filter((c) => {
      const id = c.id;
      const code = c.code;
      const idSelected = id && selectedIds.includes(id);
      const codeSelected = code && selectedCodes.includes(code);
      return !idSelected && !codeSelected;
    });
    setAvailable(availableList);
    return list;
  };


  useEffect(() => { loadRegions(); }, [page, pageSize]);

  /* -------------------- 模态框 -------------------- */
  const openCreate = async () => {
    setEditing(null);
    setSelected([]);
    await loadCountries(undefined, []);
    form.resetFields();
    setShowModal(true);
  };

  const openEdit = async (r) => {
    setEditing(r);
    const regionCountries = r.countries || [];
    const list = await loadCountries(r.id, regionCountries);
    const normalizedSelected = regionCountries.map((rc) => {
      const matched = list.find((c) => c.code === rc.code);
      return matched ? matched : { ...rc };
    });
    setSelected(normalizedSelected);

    form.setFieldsValue({
      name: r.name,
      nameEn: r.nameEn,
      code: r.code,
      description: r.description,
      sort: r.sort ?? 0,
      status: r.status ?? 1,
    });

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    form.resetFields();
    setSelected([]);
    setAvailable([]);
  };

  /* -------------------- 国家选择 -------------------- */
  const addCountry = (c) => {
    setSelected((s) => [...s, c]);
    setAvailable((a) => a.filter((i) => (i.id || i._id) !== (c.id || c._id)));
  };

  const removeCountry = (key) => {
    const removed = selected.find((c) => (c.id || c._id) === key || c.code === key);
    setSelected((s) => s.filter((c) => (c.id || c._id) !== key && c.code !== key));
    if (removed) setAvailable((a) => [...a, removed]);
  };

  /* -------------------- 提交 -------------------- */
  const handleSave = async (values) => {
    const payload = {
      ...values,
      countryIds: selected.map((c) => c.id || c._id),
    };
    try {
      if (editing) {
        await updateRegion(editing.id, payload);
        message.success('地区更新成功');
      } else {
        await createRegion(payload);
        message.success('地区创建成功');
      }
      closeModal();
      loadRegions();
    } catch (e) {
      message.error(editing ? '更新失败' : '创建失败');
    }
  };

  /* -------------------- 删除 -------------------- */
  const confirmDel = async () => {
    try {
      await deleteRegion(delId);
      message.success('已删除');
      setShowDel(false);
      loadRegions();
    } catch {
      message.error('删除失败');
    }
  };

  /* -------------------- 状态切换 -------------------- */
  const toggleStatus = async (r) => {
    try {
      const { status } = await toggleRegionStatus(r.id);
      setRegions((prev) => prev.map((i) => (i.id === r.id ? { ...i, status } : i)));
      message.success(`已${status === 1 ? '启用' : '禁用'}`);
    } catch {
      message.error('状态切换失败');
    }
  };

  /* -------------------- 表格列 -------------------- */
  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    {
      title: '地区名称',
      dataIndex: 'name',
      render: (_, r) => (
        <div>
          <div className="font-medium">{r.name}</div>
          {r.nameEn && <div className="text-gray-500 text-sm">{r.nameEn}</div>}
        </div>
      ),
    },
    {
      title: '关联国家',
      dataIndex: 'countries',
      render: (list) =>
        list?.length ? (
          <Space wrap>
            {list.map((c) => (
              <Tag key={c.id || c._id} icon={<GlobalOutlined />} color="blue">
                {c.name} ({c.code})
              </Tag>
            ))}
          </Space>
        ) : (
          <span className="text-gray-400">暂无关联</span>
        ),
    },
    { title: '顺序', dataIndex: 'sort', width: 90, render: (s) => s ?? 0 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (s) => (
        <Tag color={s === 1 ? 'green' : 'red'}>{s === 1 ? '启用' : '禁用'}</Tag>
      ),
    },
    {
      title: '操作',
      width: 220,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
            编辑
          </Button>
          <Button
            size="small"
            icon={r.status === 1 ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={() => toggleStatus(r)}
          >
            {r.status === 1 ? '禁用' : '启用'}
          </Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => { setDelId(r.id); setShowDel(true); }}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  /* -------------------- 批量导入 -------------------- */
  const openImportModal = () => {
    setCsvText('');
    setImportRows([]);
    setImportErrors([]);
    setImportModalVisible(true);
  };

  const closeImportModal = () => {
    setImportModalVisible(false);
    setCsvText('');
    setImportRows([]);
    setImportErrors([]);
    setImportLoading(false);
  };

  const normalizeStatus = (val) => {
    const s = String(val || '').trim().toLowerCase();
    if (['1', 'enabled', 'active', 'true', '启用'].includes(s)) return 1;
    if (['0', 'disabled', 'inactive', 'false', '禁用'].includes(s)) return 0;
    return 1;
  };

  const parseCsv = (text) => {
    const errors = [];
    const rows = [];
    const stripBom = (s) => String(s || '').replace(/^\ufeff/, '');
    const lines = stripBom(String(text || '')).trim().split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length === 0) return { rows, errors };

    const firstCols = lines[0].split(',').map(c => stripBom(c).trim());
    const lcCols = firstCols.map(c => c.toLowerCase());
    const containsName = lcCols.some(c => c.includes('name') || c.includes('名称'));
    const containsNameEn = lcCols.some(c => c.includes('nameen') || c.includes('英文'));
    const containsCode = lcCols.some(c => c.includes('code') || c.includes('代码'));
    const hasHeader = (containsName && containsNameEn && containsCode);
    const startIdx = hasHeader ? 1 : 0;
    
    for (let i = startIdx; i < lines.length; i++) {
      const raw = lines[i];
      const cols = raw.split(',').map(c => stripBom(c).trim());
      const name = cols[0] || '';
      const nameEn = cols[1] || '';
      const code = cols[2] || '';
      const sort = parseInt(cols[3]) || 0;
      const status = normalizeStatus(cols[4] || 'enabled');
      
      if (!name || !nameEn || !code) {
        errors.push(`第 ${i + 1} 行：name/nameEn/code 不能为空`);
        continue;
      }
      rows.push({ name, nameEn, code, sort, status });
    }
    return { rows, errors };
  };

  const handleCsvTextChange = (e) => {
    const text = e.target.value;
    setCsvText(text);
    const { rows, errors } = parseCsv(text);
    setImportRows(rows);
    setImportErrors(errors);
  };

  const decodeCsvFile = async (file) => {
    try {
      const buffer = await file.arrayBuffer();
      const tryDecode = (label) => {
        try {
          return new TextDecoder(label, { fatal: false }).decode(buffer);
        } catch (e) {
          return null;
        }
      };
      let text = tryDecode('utf-8');
      const hasReplacement = (s) => (s.match(/\uFFFD/g) || []).length > 0 || /����/.test(s);
      const hasChinese = (s) => /[\u4e00-\u9fa5]/.test(s);
      if (!text || hasReplacement(text)) {
        text = tryDecode('gbk') || tryDecode('gb18030') || text || '';
      }
      if ((!hasChinese(text)) && hasReplacement(text)) {
        text = tryDecode('utf-8') || text;
      }
      return text || '';
    } catch (err) {
      console.error('读取文件失败: ', err);
      message.error('读取文件失败');
      return '';
    }
  };

  const handleCsvFileUpload = (file) => {
    if (!file) return false;
    const isCsv = file.type === 'text/csv' || /\.csv$/i.test(file.name);
    if (!isCsv) {
      message.error('请上传 CSV 文件');
      return Upload.LIST_IGNORE || false;
    }
    const isLt10M = file.size <= 10 * 1024 * 1024;
    if (!isLt10M) {
      message.error('CSV 文件大小不能超过 10MB');
      return Upload.LIST_IGNORE || false;
    }
    (async () => {
      const text = await decodeCsvFile(file);
      setCsvText(text);
      const { rows, errors } = parseCsv(text);
      setImportRows(rows);
      setImportErrors(errors);
    })();
    return false;
  };

  const handleImportSubmit = async () => {
    if (!importRows.length) {
      message.warning('没有可导入的数据');
      return;
    }
    setImportLoading(true);
    const result = await importRegions(importRows);
    setImportLoading(false);
    if (!result?.success) {
      message.error(result?.message || '导入失败');
      return;
    }
    message.success(result?.message || `导入成功（${importRows.length} 条）`);
    closeImportModal();
    await loadRegions();
  };

  /* -------------------- UI -------------------- */
  return (
    <div>
      <Card title="地区配置">
        <Flex wrap gap="small" style={{ marginBottom: '16px' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            新增地区
          </Button>
          <Button style={{ marginLeft: 8 }} onClick={openImportModal}>
            批量导入
          </Button>
        </Flex>

        <Spin spinning={loading}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={regions}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
            }}
          />
        </Spin>
      </Card>

      {/* 新增/编辑 */}
      <Modal
        title={editing ? '编辑地区' : '创建地区'}
        open={showModal}
        onCancel={closeModal}
        footer={null}
        width={560}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="地区名称" name="name" rules={[{ required: true }]}>
                <Input placeholder="请输入地区名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="地区代码" name="code" rules={[{ required: true }]}>
                <Input placeholder="如：CN、US" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="英文名称" name="nameEn">
                <Input placeholder="请输入英文名称" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="排序" name="sort">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="状态" name="status">
                <Select>
                  <Option value={1}>启用</Option>
                  <Option value={0}>禁用</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="描述" name="description">
            <TextArea rows={3} placeholder="请输入地区描述" />
          </Form.Item>

          <Divider orientation="left"><GlobalOutlined /> 关联国家</Divider>

          {selected.length > 0 && (
            <Card size="small" style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 8 }}><strong>已选择（{selected.length}）</strong></div>
              <Space wrap>
                {selected.map((c) => (
                  <Tag
                    key={c.id || c._id}
                    closable
                    onClose={() => removeCountry(c.id || c._id || c.code)}
                    color="blue"
                  >
                    {c.name}（{c.code}）
                  </Tag>
                ))}
              </Space>
            </Card>
          )}

          {available.length > 0 && (
            <Card size="small">
              <div style={{ marginBottom: 8 }}><strong>可选择（{available.length}）</strong></div>
              <Space wrap>
                {available.map((c) => (
                  <Button
                    key={c.id || c._id}
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => addCountry(c)}
                  >
                    {c.name}（{c.code}）
                  </Button>
                ))}
              </Space>
            </Card>
          )}

          {available.length === 0 && selected.length === 0 && (
            <Alert message="暂无可用国家" type="info" showIcon />
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right', marginTop: 16 }}>
            <Space>
              <Button onClick={closeModal}>取消</Button>
              <Button type="primary" htmlType="submit">
                {editing ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 删除确认 */}
      <Modal
        title="确认删除"
        open={showDel}
        onOk={confirmDel}
        onCancel={() => setShowDel(false)}
        okType="danger"
      >
        <p>确定要删除这个地区吗？</p>
      </Modal>

      {/* 批量导入模态框 */}
      <Modal
        title="批量导入地区"
        open={importModalVisible}
        onCancel={closeImportModal}
        onOk={handleImportSubmit}
        okText="导入并保存"
        confirmLoading={importLoading}
        width={700}
      >
        <div style={{ marginBottom: 12 }}>
          <Space>
            <Button type="link" onClick={downloadRegionTemplate}>
              下载CSV模板
            </Button>
          </Space>
        </div>
        <div style={{ marginBottom: 12 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Upload.Dragger
              accept=".csv"
              multiple={false}
              showUploadList={false}
              beforeUpload={handleCsvFileUpload}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">拖放 CSV 文件到此处，或点击上传</p>
              <p className="ant-upload-hint">支持的格式: .csv（最大 10MB）</p>
            </Upload.Dragger>

            <div>
              <p style={{ color: '#555', marginTop: 8, marginBottom: 4 }}>CSV 文件格式要求：</p>
              <ul style={{ paddingLeft: 18, color: '#666' }}>
                <li>第一行为表头: name,nameEn,code,sort,status（可选）</li>
                <li>状态支持: 启用/禁用 或 enabled/disabled 或 1/0</li>
                <li>示例: 亚洲,Asia,asia,1,enabled</li>
              </ul>
            </div>

            <div>
              <p style={{ color: '#666', marginBottom: 8 }}>或直接粘贴CSV内容（包含或不包含表头）：name,nameEn,code,sort,status</p>
              <TextArea rows={6} value={csvText} onChange={handleCsvTextChange} placeholder="示例：\n亚洲,Asia,asia,1,enabled\n欧洲,Europe,europe,2,enabled" />
            </div>
          </Space>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Card size="small" title={`预览（有效 ${importRows.length} 条${importErrors.length ? `，错误 ${importErrors.length} 条` : ''}）`}>
            {importErrors.length > 0 && (
              <div style={{ color: '#c00', marginBottom: 8 }}>
                {importErrors.slice(0, 5).map((err, idx) => (
                  <div key={idx}>{err}</div>
                ))}
                {importErrors.length > 5 && <div>更多错误已隐藏...</div>}
              </div>
            )}
            <Table
              size="small"
              columns={[
                { title: '地区名称', dataIndex: 'name' },
                { title: '英文名', dataIndex: 'nameEn' },
                { title: '代码', dataIndex: 'code' },
                { title: '排序', dataIndex: 'sort' },
                { title: '状态', dataIndex: 'status', render: (s) => (s === 1 ? '启用' : '禁用') }
              ]}
              dataSource={importRows.map((r, i) => ({ ...r, _key: i }))}
              rowKey="_key"
              pagination={{ pageSize: 5 }}
            />
          </Card>
        </div>
      </Modal>
    </div>
  );
}